import type { UpgradePromptContext } from '@/types/subscription';

/** Theme color scale per upgrade context — every context gets its own. */
export const UPGRADE_CONTEXT_COLORS: Record<UpgradePromptContext, string> = {
  tasteInsights: 'purple',
  unlimitedLists: 'teal',
  advancedVoting: 'orange',
  groupCapacity: 'blue',
  issueArchive: 'pink',
  // cyan, pinned to .700 (see `SHADES_BY_CONTEXT`). Not green: `--status-green`
  // is a status/freshness hue, and the "Save 33% with yearly" callout in the
  // same modal is green, so a green context would stop the savings box
  // standing out. cyan.700 is #0E7490 — a deep petrol blue, well clear of
  // `unlimitedLists`' teal.500 (#2A9D8F; the old cyan.500 read as teal beside
  // it and put white text on a 2.43:1 fill).
  dailyCadence: 'cyan',
};

/**
 * The shades of a context's color scale used in the modal:
 *
 * - `solid` — wherever WHITE text or an icon sits ON the color: the banner
 *   gradient's first stop (which also carries the 12px whiteAlpha.900
 *   subtext), the primary CTA and the selected tier toggle.
 * - `hover` — `solid`'s hover/active step (also white text on it).
 * - `text` — the accent ON THE WHITE card in light mode: outline-button
 *   text/border, check icons and the spinner.
 * - `textDark` — the same accent ON THE DARK card (`--card-bg` #242730) in dark
 *   mode. Mode-paired because no single shade of a scale clears 4.5:1 against
 *   both white and #242730 (a shade dark enough for white is too dark for the
 *   dark card).
 *
 * Only `tasteInsights` (purple.500 = 5.70:1 with white) clears 4.5:1 at the
 * default `.500`, so the contexts below pin their own `solid`/`hover`/`text`.
 * Contrast is computed from the real theme scales in
 * `src/types/__tests__/upgradePromptCopy.test.ts`.
 *
 * KNOWN, PRE-EXISTING failures of the unchanged defaults, tracked separately
 * (each context's own line is where to fix it): `unlimitedLists` (teal),
 * `advancedVoting` (orange) and `groupCapacity` (blue) put white on `.500`
 * (3.32 / 2.84 / 3.68:1), and teal.600 / orange.600 as light-mode accent text
 * are 3.74 / 3.56:1.
 */
const SHADES_BY_CONTEXT: Partial<Record<UpgradePromptContext, UpgradeShades>> = {
  // `solid` is .700, not .600: white on pink.600 is 4.60:1, but the banner's
  // 12px subtext is whiteAlpha.900 over the fill, which is only 3.95:1 on
  // pink.600 and 5.12:1 on pink.700. white on pink.700 6.04:1, on pink.800
  // 7.88:1. `text` is .700 too: pink.600 as accent is 4.60:1 on white but
  // only 4.21:1 on its own .50 hover fill and 3.91:1 on the .100 press fill.
  issueArchive: { solid: 700, hover: 800, text: 700, textDark: 300 },
  // white on cyan.700 5.36:1, on cyan.800 7.27:1; cyan.700 on white 5.36:1.
  dailyCadence: { solid: 700, hover: 800, text: 700, textDark: 300 },
};

export interface UpgradeShades {
  solid: number;
  hover: number;
  text: number;
  textDark: number;
}

// `.300` on the dark card: 6.09:1 (purple) to 10.29:1 (cyan) across all six scales, and still >= 4.5:1 on the neutral whiteAlpha hover/press fills. (`.400` is 4.58:1 on the card for purple but 3.6-3.8:1 on those fills.)
const DEFAULT_SHADES: UpgradeShades = { solid: 500, hover: 600, text: 600, textDark: 300 };

export function getUpgradeContextShades(context: UpgradePromptContext): UpgradeShades {
  return SHADES_BY_CONTEXT[context] ?? DEFAULT_SHADES;
}
